import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { compact, startCase } from 'lodash-es';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '@env';
import { constants } from '@shared/constants';
import { CustomHttpParamEncoder } from '@shared/encoder';
import {
  AddCounterpartyParams,
  Counterparty,
  CounterpartyList,
  CounterpartyListRaw,
  CounterpartyRaw,
  RequestPageParams,
  UpdateCounterpartyParams,
} from '@shared/models';
import { BackendService } from '@shared/services/backend.service';
import { AuditActions } from '@shared/store';
import { toPhone, toTitleCase } from '@shared/utils';

@Injectable({
  providedIn: 'root',
})
export class CounterpartyService {
  constructor(
    private backendService: BackendService,
    private store: Store,
  ) {}

  public getCounterparty(
    { page, size, searchString, sortParams }: RequestPageParams,
    accountHolderId?: string,
  ): Observable<CounterpartyList> {
    const pageSize = size || constants.TABLE_ROWS;
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

    params = params.append('size', pageSize);

    if (page) {
      params = params.append('page', page);
    }

    if (searchString) {
      params = params.append('search', searchString);
    }

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    if (accountHolderId) {
      params = params.append('accountHolderId', accountHolderId);
    }

    return this.backendService
      .get<CounterpartyListRaw>(`${environment.accountFlowService}/v1/counterparties`, {
        params,
      })
      .pipe(
        map((response) => {
          const { content = [], totalElements = 0 } = response || {};

          return {
            items: content?.map((item) => CounterpartyService.mapCounterparty(item)),
            totalElements,
          };
        }),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  public addCounterparty(recipientData: AddCounterpartyParams): Observable<Counterparty> {
    return this.backendService.post<CounterpartyRaw>(`${environment.accountFlowService}/v1/counterparties`, recipientData).pipe(
      map((recipient) => CounterpartyService.mapCounterparty(recipient)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getCounterpartyById(recipientId: Counterparty['id']): Observable<Counterparty> {
    return this.backendService.get<CounterpartyRaw>(`${environment.accountFlowService}/v1/counterparties/${recipientId}`).pipe(
      tap({
        next: (recipient) => {
          const { createdBy, updatedBy } = recipient;
          this.store.dispatch(AuditActions.loadTeamMembers({ ids: compact([createdBy, updatedBy]) }));
        },
      }),
      map((recipient) => CounterpartyService.mapCounterparty(recipient)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public updateCounterparty(recipientId: Counterparty['id'], counterpartyData: UpdateCounterpartyParams): Observable<Counterparty> {
    return this.backendService
      .update<CounterpartyRaw>(`${environment.accountFlowService}/v1/counterparties/${recipientId}`, counterpartyData)
      .pipe(
        map((recipient) => CounterpartyService.mapCounterparty(recipient)),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  private static mapCounterparty(counterparty: CounterpartyRaw): Counterparty {
    return {
      ...counterparty,
      displayName: counterparty.businessName || compact([counterparty.firstName, counterparty.lastName]).join(' '),
      email: counterparty.email?.toLowerCase(),
      counterpartyTypeName: toTitleCase(counterparty.counterpartyType),
      phoneNumber: toPhone(counterparty.phoneNumber),
      shortId: counterparty.id.split('-')[0],
      avatarInitials: startCase(counterparty.businessName?.charAt(0) || counterparty.firstName.charAt(0)),
    };
  }
}
